import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { checkFeatureFlagGate, featureFlags } from "@/utils/feature-flags";

import { type SocialProvider } from "../../../utils";
import { useAccountCreationContext } from "../../hooks";

import { LogoButton, ProviderLogo } from "./components";

export const Social = ({
    providers
}: {
    providers?: { [K in SocialProvider]?: string };
}) => {
    const intl = useIntl();
    const { onClickSocialButton } = useAccountCreationContext();

    if (providers === undefined) {
        providers = {
            google: "Google",
            microsoft: "Microsoft",
            apple: "Apple",
            slack: "Slack"
        };
    }

    const buttons = Object.entries(providers);

    // when there are 3 buttons, we display in a single row and visually hide the text labels
    const rowStyle =
        buttons.length === 3 &&
        checkFeatureFlagGate(featureFlags.signupAccountCreationSocialRowStyle);

    /* eslint-disable jsx-a11y/no-redundant-roles */
    /* added role="list" to ul for safari https://developer.mozilla.org/en-US/docs/Web/CSS/list-style#accessibility  */
    return (
        <ul
            css={[
                buttonListCss,
                buttons.length > 1 && !rowStyle && buttonGridCss,
                rowStyle && buttonRowCss
            ]}
            role="list"
        >
            {buttons.map(([provider, label]) => (
                <li key={provider}>
                    <LogoButton
                        aria-label={intl.formatMessage(
                            {
                                id: "social.label.signup",
                                defaultMessage: "sign up with {provider}"
                            },
                            {
                                provider: label
                            }
                        )}
                        onClick={() => onClickSocialButton(provider)}
                    >
                        <ProviderLogo logo={provider} />
                        <span css={[rowStyle && srOnlyCss]}>{label}</span>
                    </LogoButton>
                </li>
            ))}
        </ul>
    );
};

const buttonGridCss = css({
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(2, 1fr)"
});

const buttonListCss = css({
    listStyle: "none",
    margin: 0,
    padding: 0
});

const buttonRowCss = css({
    display: "grid",
    gap: 16,
    gridAutoFlow: "column"
});

const srOnlyCss = css({
    position: "absolute",
    insetInlineStart: -10000,
    insetBlockStart: "auto",
    width: 1,
    height: 1,
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    whiteSpace: "nowrap"
});
